import React, { useState, useEffect, useCallback } from 'react';
import styles from './StoreView.module.scss';
import { normalizeToEnglish } from '../../utils/digitConverter';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext.jsx';

const StoreView = ({ refreshTrigger = 0 }) => {
    const { makeAuthenticatedRequest } = useAuth();
    const [storeData, setStoreData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalItems, setTotalItems] = useState(0);

    // Filter states
    const [filters, setFilters] = useState({
        miti: '',
        dakshila_no: '',
        specification: '',
        saman_ko_pahichan: '',
        model_no: '',
        item_name: '',
        utpadan_garnedesh: '',
        size: '',
        anumit_aayu: '',
        saman_prabidhik_srot: '',
        parikshan: '',
        prati_ekai_ko_dar: '',
        jamma_paral_mulya: '',
        pariman: '',
        jamma_paral_mulya2: '',
        pariman2: '',
        jamma_paral_mulya3: '',
        kaifiyat: ''
    });

    const [showFilters, setShowFilters] = useState(false);
    const [globalSearch, setGlobalSearch] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchStoreData = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true);
            }
            const offset = (currentPage - 1) * itemsPerPage;
            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/?limit=${itemsPerPage}&offset=${offset}`);
            if (response.ok) {
                const data = await response.json();
                                        setStoreData(data.results || data);
        setTotalItems(data.count || data.length);
            } else {
                setError('Failed to fetch data');
            }
        } catch (error) {
            setError('Error fetching data');
            console.error('Error:', error);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [currentPage, itemsPerPage, makeAuthenticatedRequest]);

    const applyFilters = useCallback(() => {
        let filtered = [...storeData];

        // Apply global search with mixed digit support
        if (globalSearch.trim()) {
            const searchTerm = normalizeToEnglish(globalSearch.toLowerCase());
            filtered = filtered.filter(item => 
                Object.values(item).some(value => {
                    if (!value) return false;
                    const normalizedValue = normalizeToEnglish(value.toString().toLowerCase());
                    return normalizedValue.includes(searchTerm);
                })
            );
        }

        // Apply individual column filters with mixed digit support
        Object.keys(filters).forEach(key => {
            if (filters[key].trim()) {
                const filterValue = normalizeToEnglish(filters[key].toLowerCase());
                filtered = filtered.filter(item => {
                    if (!item[key]) return false;
                    const normalizedValue = normalizeToEnglish(item[key].toString().toLowerCase());
                    return normalizedValue.includes(filterValue);
                });
            }
        });

        setFilteredData(filtered);
    }, [storeData, globalSearch, filters]);

    // Apply filters whenever filters or data changes
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Fetch data when pagination changes or refresh is triggered
    useEffect(() => {
        fetchStoreData();
    }, [currentPage, itemsPerPage, fetchStoreData]);

    // Handle auto-refresh after upload (without loading indicator)
    useEffect(() => {
        if (refreshTrigger > 0) {
            setRefreshing(true);
            fetchStoreData(false).finally(() => {
                setRefreshing(false);
            });
        }
    }, [refreshTrigger, fetchStoreData]);

    const handleFilterChange = (column, value) => {
        setFilters(prev => ({
            ...prev,
            [column]: value
        }));
        setCurrentPage(1); // Reset to first page when filtering
    };

    const handleGlobalSearch = (value) => {
        setGlobalSearch(value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const clearAllFilters = () => {
        setFilters({
            miti: '',
            dakshila_no: '',
            specification: '',
            saman_ko_pahichan: '',
            model_no: '',
            utpadan_garnedesh: '',
            size: '',
            anumit_aayu: '',
            saman_prabidhik_srot: '',
            parikshan: '',
            prati_ekai_ko_dar: '',
            jamma_paral_mulya: '',
            pariman: '',
            jamma_paral_mulya2: '',
            pariman2: '',
            jamma_paral_mulya3: '',
            kaifiyat: ''
        });
        setGlobalSearch('');
        setCurrentPage(1);
    };

    const handlePrint = () => {
        // Create print-friendly HTML with exact table structure - EXACTLY matching the image
        const printHTML = `
            <html>
            <head>
                <title>Store Management Data</title>
                <style>
                    @media print {
                        body { margin: 0; padding: 20px; }
                        .print-header { text-align: center; font-size: 24px; font-weight: bold; margin-bottom: 20px; }
                        table { border-collapse: collapse; width: 100%; font-size: 12px; }
                        th, td { border: 1px solid #000; padding: 6px; text-align: center; vertical-align: middle; }
                        .main-header { background-color: #4a5568 !important; color: white !important; font-weight: bold; }
                        .sub-header { background-color: #e2e8f0 !important; font-weight: bold; }
                        .column-numbers { background-color: #90EE90 !important; font-weight: bold; }
                        .data-row:nth-child(even) { background-color: #90EE90 !important; }
                        .data-row:nth-child(odd) { background-color: white !important; }
                        .no-print { display: none !important; }
                    }
                    body { font-family: Arial, sans-serif; }
                    .print-header { text-align: center; font-size: 24px; font-weight: bold; margin-bottom: 20px; }
                    table { border-collapse: collapse; width: 100%; font-size: 12px; }
                    th, td { border: 1px solid #000; padding: 6px; text-align: center; vertical-align: middle; }
                    .main-header { background-color: #4a5568; color: white; font-weight: bold; }
                    .sub-header { background-color: #e2e8f0; font-weight: bold; }
                    .column-numbers { background-color: #90EE90; font-weight: bold; }
                    .data-row:nth-child(even) { background-color: #90EE90; }
                    .data-row:nth-child(odd) { background-color: white; }
                    .print-button { 
                        position: fixed; 
                        top: 20px; 
                        right: 20px; 
                        padding: 10px 20px; 
                        background: #4a5568; 
                        color: white; 
                        border: none; 
                        border-radius: 5px; 
                        cursor: pointer; 
                        font-size: 16px;
                    }
                    .print-button:hover { background: #2d3748; }
                    @media print { .print-button { display: none; } }
                </style>
            </head>
            <body>
                <button class="print-button" onclick="window.print()">🖨️ Print</button>
                <div class="print-header">Store Management Data</div>
                <table>
                    <thead>
                        <tr class="main-header">
                            <th rowspan="2">क्र.सं.</th>
                            <th rowspan="2">मिति</th>
                            <th rowspan="2">दाखिला न/ हस्तान्तरण नं.</th>
                            <th rowspan="2">स्पेसिफिकेसन</th>
                            <th rowspan="2">सामानको पहिचान नं</th>
                            <th rowspan="2">मोडल न</th>
                            <th rowspan="2">जिन्सी सामानको नाम</th>
                            <th colspan="4">विवरण</th>
                            <th colspan="3">आम्दानी</th>
                            <th colspan="2">हस्तान्तरण/मिन्हा/निःसर्ग</th>
                            <th colspan="2">बाँकी</th>
                            <th rowspan="2">कैफियत</th>
                        </tr>
                        <tr class="sub-header">
                            <th>उत्पादन गर्ने देश वा कम्पनीको नाम</th>
                            <th>साइज</th>
                            <th>अनुमानित आयु</th>
                            <th>सामान प्राप्तिको स्रोत</th>
                            <th>परिमाण</th>
                            <th>प्रति एकाइको दर (अन्य खर्चको समेतका आधारमा)</th>
                            <th>जम्मा परल मूल्य</th>
                            <th>परिमाण</th>
                            <th>जम्मा परल मूल्य</th>
                            <th>परिमाण</th>
                            <th>जम्मा परल मूल्य</th>
                        </tr>
                        <tr class="column-numbers">
                            <th></th>
                            <th>१</th>
                            <th>२</th>
                            <th>३</th>
                            <th>४</th>
                            <th>५</th>
                            <th></th>
                            <th>६</th>
                            <th>७</th>
                            <th>८</th>
                            <th>९</th>
                            <th>१०</th>
                            <th>११</th>
                            <th>१२</th>
                            <th>१३</th>
                            <th>१४</th>
                            <th>१५</th>
                            <th>१६</th>
                            <th>१७</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${filteredData.map((item, index) => `
                            <tr class="data-row">
                                <td>${startIndex + index}</td>
                                <td>${item.miti_nepali || item.miti || ''}</td>
                                <td>${item.dakshila_no || ''}</td>
                                <td>${item.specification || ''}</td>
                                <td>${item.saman_ko_pahichan || ''}</td>
                                <td>${item.model_no || ''}</td>
                                <td>${item.item_name || ''}</td>
                                <td>${item.utpadan_garnedesh || ''}</td>
                                <td>${item.size || ''}</td>
                                <td>${item.anumit_aayu || ''}</td>
                                <td>${item.saman_prabidhik_srot || ''}</td>
                                <td>${item.parikshan || ''}</td>
                                <td>${item.prati_ekai_ko_dar || ''}</td>
                                <td>${item.jamma_paral_mulya || ''}</td>
                                <td>${item.pariman || ''}</td>
                                <td>${item.jamma_paral_mulya2 || ''}</td>
                                <td>${item.pariman2 || ''}</td>
                                <td>${item.jamma_paral_mulya3 || ''}</td>
                                <td>${item.kaifiyat || ''}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        // Create a new window and write the HTML content
        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        // Focus the window but don't auto-print or close
        printWindow.focus();
    };

    const handleDeleteAll = async () => {
        if (deletePassword !== '1249') {
            setDeleteError('❌ Incorrect password!');
            return;
        }

        setIsDeleting(true);
        setDeleteError('');

        try {
            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/delete-all/`, {
                method: 'DELETE',
                body: JSON.stringify({ password: deletePassword })
            });

            if (response.ok) {
                setShowDeleteModal(false);
                setDeletePassword('');
                setDeleteError('');
                // Refresh data to show empty state
                fetchStoreData();
            } else {
                const errorData = await response.json();
                setDeleteError(`❌ ${errorData.error || 'Failed to delete data'}`);
            }
        } catch (error) {
            setDeleteError('❌ Network error occurred');
            console.error('Delete error:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const downloadExcel = () => {
        // Create HTML table with proper structure for Excel - EXACTLY matching the image
        const createExcelHTML = () => {
            const tableHTML = `
                <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
                <head>
                    <meta charset="UTF-8">
                    <style>
                        table { border-collapse: collapse; width: 100%; }
                        th, td { border: 1px solid #000; padding: 8px; text-align: center; vertical-align: middle; }
                        .main-header { background-color: #4a5568; color: white; font-weight: bold; }
                        .sub-header { background-color: #e2e8f0; font-weight: bold; }
                        .column-numbers { background-color: #90EE90; font-weight: bold; }
                        .data-row:nth-child(even) { background-color: #90EE90; }
                        .data-row:nth-child(odd) { background-color: white; }
                    </style>
                </head>
                <body>
                    <table>
                        <thead>
                            <tr class="main-header">
                                <th rowspan="2">क्र.सं.</th>
                                <th rowspan="2">मिति</th>
                                <th rowspan="2">दाखिला न/ हस्तान्तरण नं.</th>
                                <th rowspan="2">स्पेसिफिकेसन</th>
                                <th rowspan="2">सामानको पहिचान नं</th>
                                <th rowspan="2">मोडल न</th>
                                <th rowspan="2">जिन्सी सामानको नाम</th>
                                <th colspan="4">विवरण</th>
                                <th colspan="3">आम्दानी</th>
                                <th colspan="2">हस्तान्तरण/मिन्हा/निःसर्ग</th>
                                <th colspan="2">बाँकी</th>
                                <th rowspan="2">कैफियत</th>
                            </tr>
                            <tr class="sub-header">
                                <th>उत्पादन गर्ने देश वा कम्पनीको नाम</th>
                                <th>साइज</th>
                                <th>अनुमानित आयु</th>
                                <th>सामान प्राप्तिको स्रोत</th>
                                <th>परिमाण</th>
                                <th>प्रति एकाइको दर (अन्य खर्चको समेतका आधारमा)</th>
                                <th>जम्मा परल मूल्य</th>
                                <th>परिमाण</th>
                                <th>जम्मा परल मूल्य</th>
                                <th>परिमाण</th>
                                <th>जम्मा परल मूल्य</th>
                            </tr>
                            <tr class="column-numbers">
                                <th></th>
                                <th>१</th>
                                <th>२</th>
                                <th>३</th>
                                <th>४</th>
                                <th>५</th>
                                <th></th>
                                <th>६</th>
                                <th>७</th>
                                <th>८</th>
                                <th>९</th>
                                <th>१०</th>
                                <th>११</th>
                                <th>१२</th>
                                <th>१३</th>
                                <th>१४</th>
                                <th>१५</th>
                                <th>१६</th>
                                <th>१७</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredData.map((item, index) => `
                                <tr class="data-row">
                                    <td>${startIndex + index}</td>
                                    <td>${item.miti_nepali || item.miti || ''}</td>
                                    <td>${item.dakshila_no || ''}</td>
                                    <td>${item.specification || ''}</td>
                                    <td>${item.saman_ko_pahichan || ''}</td>
                                    <td>${item.model_no || ''}</td>
                                    <td>${item.item_name || ''}</td>
                                    <td>${item.utpadan_garnedesh || ''}</td>
                                    <td>${item.size || ''}</td>
                                    <td>${item.anumit_aayu || ''}</td>
                                    <td>${item.saman_prabidhik_srot || ''}</td>
                                    <td>${item.parikshan || ''}</td>
                                    <td>${item.prati_ekai_ko_dar || ''}</td>
                                    <td>${item.jamma_paral_mulya || ''}</td>
                                    <td>${item.pariman || ''}</td>
                                    <td>${item.jamma_paral_mulya2 || ''}</td>
                                    <td>${item.pariman2 || ''}</td>
                                    <td>${item.jamma_paral_mulya3 || ''}</td>
                                    <td>${item.kaifiyat || ''}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </body>
                </html>
            `;
            return tableHTML;
        };

        // Create and download Excel file
        const htmlContent = createExcelHTML();
        const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'store_management_data.xls';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Pagination calculations
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1); // Reset to first page
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    if (loading) {
        return <div className={styles.loading}>Loading data...</div>;
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    return (
        <>
            {refreshing && (
                <div className={styles.refreshing} style={{
                    position: 'fixed',
                    top: '10px',
                    right: '10px',
                    background: '#4CAF50',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    zIndex: 1000,
                    fontSize: '14px'
                }}>
                    🔄 Refreshing data...
                </div>
                         )}
             
             {/* Delete All Modal */}
             {showDeleteModal && (
                 <div style={{
                     position: 'fixed',
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: 0,
                     background: 'rgba(0, 0, 0, 0.5)',
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     zIndex: 10000
                 }}>
                     <div style={{
                         background: 'white',
                         padding: '30px',
                         borderRadius: '8px',
                         maxWidth: '400px',
                         width: '90%',
                         boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                     }}>
                         <h3 style={{ margin: '0 0 20px 0', color: '#dc3545' }}>⚠️ Delete All Data</h3>
                         <p style={{ margin: '0 0 20px 0', color: '#666' }}>
                             This action will permanently delete ALL data from the database. 
                             This action cannot be undone!
                         </p>
                         <div style={{ marginBottom: '20px' }}>
                             <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                 Enter Secret Password:
                             </label>
                             <input
                                 type="password"
                                 value={deletePassword}
                                 onChange={(e) => setDeletePassword(e.target.value)}
                                 style={{
                                     width: '100%',
                                     padding: '10px',
                                     border: '1px solid #ddd',
                                     borderRadius: '4px',
                                     fontSize: '14px'
                                 }}
                                 onKeyPress={(e) => {
                                     if (e.key === 'Enter') {
                                         handleDeleteAll();
                                     }
                                 }}
                             />
                         </div>
                         {deleteError && (
                             <div style={{
                                 color: '#dc3545',
                                 marginBottom: '15px',
                                 fontSize: '14px'
                             }}>
                                 {deleteError}
                             </div>
                         )}
                         <div style={{
                             display: 'flex',
                             gap: '10px',
                             justifyContent: 'flex-end'
                         }}>
                             <button
                                 onClick={() => {
                                     setShowDeleteModal(false);
                                     setDeletePassword('');
                                     setDeleteError('');
                                 }}
                                 disabled={isDeleting}
                                 style={{
                                     padding: '10px 20px',
                                     border: '1px solid #ddd',
                                     background: 'white',
                                     borderRadius: '4px',
                                     cursor: 'pointer'
                                 }}
                             >
                                 Cancel
                             </button>
                             <button
                                 onClick={handleDeleteAll}
                                 disabled={isDeleting || !deletePassword}
                                 style={{
                                     padding: '10px 20px',
                                     background: '#dc3545',
                                     color: 'white',
                                     border: 'none',
                                     borderRadius: '4px',
                                     cursor: isDeleting || !deletePassword ? 'not-allowed' : 'pointer',
                                     opacity: isDeleting || !deletePassword ? 0.6 : 1
                                 }}
                             >
                                 {isDeleting ? '🗑️ Deleting...' : '🗑️ Delete All'}
                             </button>
                         </div>
                     </div>
                 </div>
             )}
         <div className={styles['store-view-container']}>
            <div className={styles['view-header']}>
                <h2>Store Management Data</h2>
                <div className={styles['header-buttons']}>
                    <div className={styles['pagination-controls']}>
                        <label>
                            Items per page:
                            <select 
                                value={itemsPerPage} 
                                onChange={handleItemsPerPageChange}
                                className={styles['items-per-page']}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                            </select>
                        </label>
                    </div>
                    <button 
                        onClick={() => setShowFilters(!showFilters)}
                        className={styles['filter-btn']}
                        title="Toggle filters"
                    >
                        🔍 Filters
                    </button>
                    <button 
                        onClick={fetchStoreData}
                        className={styles['refresh-btn']}
                        title="Refresh data"
                    >
                        🔄 Refresh
                    </button>
                    <button 
                        onClick={downloadExcel}
                        className={styles['excel-btn']}
                        title="Download Excel"
                    >
                        📊 Excel
                    </button>
                                         <button 
                         onClick={handlePrint}
                         className={styles['print-btn']}
                         title="Print data"
                     >
                         🖨️ Print
                     </button>
                     <button 
                         onClick={() => setShowDeleteModal(true)}
                         className={styles['delete-all-btn']}
                         title="Delete all data"
                         style={{
                             background: '#dc3545',
                             color: 'white',
                             border: 'none',
                             padding: '8px 16px',
                             borderRadius: '4px',
                             cursor: 'pointer',
                             fontSize: '14px',
                             marginLeft: '8px'
                         }}
                     >
                         🗑️ Delete All
                     </button>
                </div>
            </div>

            {/* Global Search */}
            <div className={styles['global-search']}>
                <input
                    type="text"
                    placeholder="🔍 Search across all columns..."
                    value={globalSearch}
                    onChange={(e) => handleGlobalSearch(e.target.value)}
                    className={styles['search-input']}
                />
                <button 
                    onClick={clearAllFilters}
                    className={styles['clear-filters-btn']}
                    title="Clear all filters"
                >
                    🗑️ Clear All
                </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
                <div className={styles['filters-panel']}>
                    <div className={styles['filters-grid']}>
                        <div className={styles['filter-group']}>
                            <label>मिति (Date)</label>
                            <input
                                type="text"
                                value={filters.miti}
                                onChange={(e) => handleFilterChange('miti', e.target.value)}
                                placeholder="Filter by date..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>दाखिला नं (Entry No.)</label>
                            <input
                                type="text"
                                value={filters.dakshila_no}
                                onChange={(e) => handleFilterChange('dakshila_no', e.target.value)}
                                placeholder="Filter by entry no..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>स्पेसिफिकेसन (Specification)</label>
                            <input
                                type="text"
                                value={filters.specification}
                                onChange={(e) => handleFilterChange('specification', e.target.value)}
                                placeholder="Filter by specification..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>सामानको पहिचान नं (Item ID)</label>
                            <input
                                type="text"
                                value={filters.saman_ko_pahichan}
                                onChange={(e) => handleFilterChange('saman_ko_pahichan', e.target.value)}
                                placeholder="Filter by item ID..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>मोडल नं (Model No.)</label>
                            <input
                                type="text"
                                value={filters.model_no}
                                onChange={(e) => handleFilterChange('model_no', e.target.value)}
                                placeholder="Filter by model no..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>जिन्सी सामानको नाम (Item Name)</label>
                            <input
                                type="text"
                                value={filters.item_name}
                                onChange={(e) => handleFilterChange('item_name', e.target.value)}
                                placeholder="Filter by item name..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>उत्पादन गर्ने देश (Country)</label>
                            <input
                                type="text"
                                value={filters.utpadan_garnedesh}
                                onChange={(e) => handleFilterChange('utpadan_garnedesh', e.target.value)}
                                placeholder="Filter by country..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>साइज (Size)</label>
                            <input
                                type="text"
                                value={filters.size}
                                onChange={(e) => handleFilterChange('size', e.target.value)}
                                placeholder="Filter by size..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>अनुमानित आयु (Estimated Age)</label>
                            <input
                                type="text"
                                value={filters.anumit_aayu}
                                onChange={(e) => handleFilterChange('anumit_aayu', e.target.value)}
                                placeholder="Filter by age..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>स्रोत (Source)</label>
                            <input
                                type="text"
                                value={filters.saman_prabidhik_srot}
                                onChange={(e) => handleFilterChange('saman_prabidhik_srot', e.target.value)}
                                placeholder="Filter by source..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>परिमाण (Quantity)</label>
                            <input
                                type="text"
                                value={filters.parikshan}
                                onChange={(e) => handleFilterChange('parikshan', e.target.value)}
                                placeholder="Filter by quantity..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>दर (Rate)</label>
                            <input
                                type="text"
                                value={filters.prati_ekai_ko_dar}
                                onChange={(e) => handleFilterChange('prati_ekai_ko_dar', e.target.value)}
                                placeholder="Filter by rate..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>जम्मा मूल्य (Total Value)</label>
                            <input
                                type="text"
                                value={filters.jamma_paral_mulya}
                                onChange={(e) => handleFilterChange('jamma_paral_mulya', e.target.value)}
                                placeholder="Filter by total value..."
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>कैफियत (Remarks)</label>
                            <input
                                type="text"
                                value={filters.kaifiyat}
                                onChange={(e) => handleFilterChange('kaifiyat', e.target.value)}
                                placeholder="Filter by remarks..."
                            />
                        </div>
                    </div>
                </div>
            )}
            
            <div className={styles['table-wrapper']}>
                <div className={styles['table-container']}>
                    <table className={styles['store-table']}>
                        <thead>
                            <tr className={styles['main-header']}>
                                <th rowSpan="2">क्र.सं.</th>
                                <th rowSpan="2">मिति</th>
                                <th rowSpan="2">दाखिला न/ हस्तान्तरण नं.</th>
                                <th rowSpan="2">स्पेसिफिकेसन</th>
                                <th rowSpan="2">सामानको पहिचान नं</th>
                                <th rowSpan="2">मोडल न</th>
                                <th rowSpan="2">जिन्सी सामानको नाम</th>
                                <th colSpan="4">विवरण</th>
                                <th colSpan="3">आम्दानी</th>
                                <th colSpan="2">हस्तान्तरण/मिन्हा/निःसर्ग</th>
                                <th colSpan="2">बाँकी</th>
                                <th rowSpan="2">कैफियत</th>
                            </tr>
                            <tr className={styles['sub-header']}>
                                <th>उत्पादन गर्ने देश वा कम्पनीको नाम</th>
                                <th>साइज</th>
                                <th>अनुमानित आयु</th>
                                <th>सामान प्राप्तिको स्रोत</th>
                                <th>परिमाण</th>
                                <th>प्रति एकाइको दर (अन्य खर्चको समेतका आधारमा)</th>
                                <th>जम्मा परल मूल्य</th>
                                <th>परिमाण</th>
                                <th>जम्मा परल मूल्य</th>
                                <th>परिमाण</th>
                                <th>जम्मा परल मूल्य</th>
                            </tr>
                            <tr className={styles['column-numbers']}>
                                <th></th>
                                <th>१</th>
                                <th>२</th>
                                <th>३</th>
                                <th>४</th>
                                <th>५</th>
                                <th></th>
                                <th>६</th>
                                <th>७</th>
                                <th>८</th>
                                <th>९</th>
                                <th>१०</th>
                                <th>११</th>
                                <th>१२</th>
                                <th>१३</th>
                                <th>१४</th>
                                <th>१५</th>
                                <th>१६</th>
                                <th>१७</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="19" className={styles['no-data']}>
                                        कोही डाटा फेला परेन (No data found)
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((item, index) => (
                                    <tr key={item.id} className={styles['data-row']}>
                                        <td>{startIndex + index}</td>
                                        <td>{item.miti_nepali || item.miti}</td>
                                        <td>{item.dakshila_no}</td>
                                        <td>{item.specification}</td>
                                        <td>{item.saman_ko_pahichan}</td>
                                        <td>{item.model_no}</td>
                                        <td>{item.item_name}</td>
                                        <td>{item.utpadan_garnedesh}</td>
                                        <td>{item.size}</td>
                                        <td>{item.anumit_aayu}</td>
                                        <td>{item.saman_prabidhik_srot}</td>
                                        <td>{item.parikshan}</td>
                                        <td>{item.prati_ekai_ko_dar}</td>
                                        <td>{item.jamma_paral_mulya}</td>
                                        <td>{item.pariman}</td>
                                        <td>{item.jamma_paral_mulya2}</td>
                                        <td>{item.pariman2}</td>
                                        <td>{item.jamma_paral_mulya3}</td>
                                        <td>{item.kaifiyat}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
                <div className={styles['pagination-container']}>
                    <div className={styles['pagination-info']}>
                        Showing {startIndex} to {endIndex} of {totalItems} entries
                        {(globalSearch || Object.values(filters).some(f => f)) && (
                            <span className={styles['filtered-info']}>
                                (Filtered from {storeData.length} total records)
                            </span>
                        )}
                    </div>
                    <div className={styles['pagination-controls']}>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={styles['pagination-btn']}
                        >
                            ← Previous
                        </button>
                        
                        {getPageNumbers().map((page, index) => (
                            <button
                                key={index}
                                onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
                                disabled={page === '...'}
                                className={`${styles['pagination-btn']} ${page === currentPage ? styles['active'] : ''} ${page === '...' ? styles['disabled'] : ''}`}
                            >
                                {page}
                            </button>
                        ))}
                        
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={styles['pagination-btn']}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default StoreView;
